import React, { useEffect, useState } from 'react';
import { Portal } from '@nile/components';
import Draggable from 'react-draggable';

const DraggablePortal = ({
  setIsDragging,
  handle,
  children,
  elementRef,
  defaultPosition,
  negativeXOffset = 0, // Pushes the component from right to left
  positiveXOffset = 0, // Pushes the component from left to right
  negativeYOffset = 0, // Pushes the component from bottom to top
  positiveYOffset = 0, // Pushes the component from top to bottom
  onDrag,
  onDragStart,
  onDragEnd,
  minX = 0, // Minimum X position in left
  minY = 0 // Minimum Y Poisiton in top
}) => {
  const [position, setPosition] = useState(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const updateInitialPosition = () => {
    if (elementRef?.current) {
      // Get the viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Get the dimensions of the draggable component
      const elementWidth = elementRef.current.offsetWidth;
      const elementHeight = elementRef.current.offsetHeight;

      // Calculate the initial position to center the component
      if (defaultPosition === 'center') {
        const x = (viewportWidth - elementWidth) / 2;
        const y = (viewportHeight - elementHeight) / 2;
        setPosition({ x, y });
      } else if (defaultPosition === 'bottom-right') {
        // Calculate initial position to bottom right
        const x = viewportWidth - 140 - negativeXOffset; // Added 140 to prevent it overlapping with intercom on initial load
        const y = viewportHeight - 75 - negativeYOffset;
        setPosition({ x, y });
      } else {
        setPosition({ x: 70, y: 0 });
      }
    }
  };

  const adjustYAxis = () => {
    // Fucntion to adjust the position If the height of the element increased and goes out of view port.
    if (position) {
      let newPosition = { ...position };
      const windowHeight = window.innerHeight;
      const elementHeight = elementRef.current.scrollHeight || 100;
      const currentY = position?.y;

      if (currentY + elementHeight > windowHeight) {
        newPosition.y =
          windowHeight - elementHeight - negativeYOffset + positiveYOffset;
      }
      if (position.y < 0) {
        newPosition.y = 0;
      }
      setPosition(newPosition);
    }
  };

  useEffect(() => {
    // Initial position setup
    updateInitialPosition();

    // Optionally, handle viewport resize to adjust the position dynamically
    window.addEventListener('resize', updateInitialPosition);
    return () => window.removeEventListener('resize', updateInitialPosition);
  }, []);

  useEffect(() => {
    if (elementRef) {
      const updateSize = () => {
        setSize({
          width: elementRef.current.offsetWidth,
          height: elementRef.current.offsetHeight
        });
      };

      // Initial size measurement
      updateSize();

      // Set up a MutationObserver to detect size changes
      const observer = new MutationObserver(() => {
        updateSize();
      });

      if (elementRef.current) {
        observer.observe(elementRef.current, {
          attributes: true,
          childList: true,
          subtree: true
        });
      }

      return () => {
        if (observer) {
          observer.disconnect();
        }
      };
    }
  }, [elementRef?.current]);

  useEffect(() => {
    adjustYAxis();
  }, [size]);

  const handleDrag = (e, data) => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const elementWidth = elementRef?.current.scrollWidth || 0;
    const elementHeight = elementRef?.current.scrollHeight || 0;

    // Calculate the max X and Y positions
    const maxX = windowWidth - elementWidth - 20;
    const maxY = windowHeight - elementHeight - 20;

    // Constrain dragging to the viewport boundaries
    let constrainedX = Math.max(0 + minX, Math.min(data.x, maxX));
    let constrainedY = Math.max(0 + minY, Math.min(data.y, maxY));

    // Update position state
    setPosition({ x: constrainedX, y: constrainedY });
    if (onDrag) {
      onDrag();
    }
  };

  const onStart = () => {
    if (setIsDragging) {
      setIsDragging(true);
    }
    if (onDragStart) {
      onDragStart();
    }
  };

  const onStop = () => {
    if (setIsDragging) {
      setIsDragging(false);
    }
    if (onDragEnd) {
      onDragEnd();
    }
  };

  const handleProp = handle ? { handle: handle } : {};

  return (
    <Portal>
      <Draggable
        onDrag={handleDrag}
        onStart={onStart}
        onStop={onStop}
        position={position}
        {...handleProp}
        // defaultPosition={initialPosition}
      >
        <div
          style={{
            width: 'fit-content',
            height: 'auto',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 2147484000,
            visibility: position ? 'visible' : 'hidden'
          }}
        >
          {children}
        </div>
      </Draggable>
    </Portal>
  );
};

export default DraggablePortal;
